"use client"

import { Fragment, useMemo, type ReactNode } from "react"
import {
  type AgentState,
  useMultibandTrackVolume,
  type TrackReferenceOrPlaceholder,
} from "../react"
import type { LocalAudioTrack, RemoteAudioTrack } from "../client"
import { cn } from "@workspace/ui/lib/utils"
import { useAgentAudioVisualizerBarAnimator } from "../hooks/use-agent-audio-visualizer-bar"

export interface AgentAudioVisualizerBarProps {
  state?: AgentState
  barCount?: number
  audioTrack?: LocalAudioTrack | RemoteAudioTrack | TrackReferenceOrPlaceholder
  className?: string
  children?: ReactNode
}

export function AgentAudioVisualizerBar({
  state = "connecting",
  barCount = 3,
  audioTrack,
  className,
  children,
}: AgentAudioVisualizerBarProps) {
  const sequencerInterval = useMemo(() => {
    switch (state) {
      case "connecting":
        return 2000 / barCount
      case "initializing":
        return 2000
      case "listening":
        return 500
      case "thinking":
        return 150
      default:
        return 1000
    }
  }, [state, barCount])

  const highlightedIndices = useAgentAudioVisualizerBarAnimator(
    state,
    barCount,
    sequencerInterval
  )

  const volumeBands = useMultibandTrackVolume(audioTrack, {
    bands: barCount,
    loPass: 100,
    hiPass: 200,
  })

  const bands = useMemo(
    () => (state === "speaking" ? volumeBands : new Array(barCount).fill(0)),
    [state, volumeBands, barCount]
  )

  return (
    <div
      data-lk-state={state}
      className={cn(
        "relative flex h-6 items-center justify-center gap-0.5",
        className
      )}
    >
      {bands.map((band: number, idx: number) =>
        children ? (
          <Fragment key={idx}>{children}</Fragment>
        ) : (
          <div
            key={idx}
            data-lk-index={idx}
            data-lk-highlighted={highlightedIndices.includes(idx)}
            style={{ height: `${Math.max(4, band * 24)}px` }}
            className="min-h-1 w-1 rounded-full bg-primary/20 transition-[height] duration-100 ease-linear data-[lk-highlighted=true]:bg-primary"
          />
        )
      )}
    </div>
  )
}
